import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Form, Input, InputNumber, Message, Modal, Popconfirm, Radio, Space, Spin } from '@arco-design/web-react';
import copy from 'copy-to-clipboard';
import styled from 'styled-components';

import WechatPayQRCode, { RECHARGE_STATUS, RechargeStatus } from '@/components/WxPay';
import i18n from '@/i18n';
import { ImageUploadItem } from '@/pages/system/ops-center/product/upload';
import { primaryColor } from '@/styles/variants';
import { getUserInfo } from '@/utils/useLocalStorage';

import { cancelRecharge, getRechargeStatus, getSystemConfig, preCreateId, rechargeRequests } from './service';

const t = i18n.t.bind(i18n);
// 预置可充金额
const PRESET_AMOUNTS = [100, 500, 1000, 2000];

const CustomModal = styled(Modal)`
  h3 {
    font-size: 16px;
    margin-bottom: 14px;
    font-weight: 600;
  }
  h4 {
    margin-bottom: 14px;
    font-weight: 500;
  }
`;

// 充值方式枚举
export const PAYMENT_METHODS = {
  WECHAT_PAY: 'WECHAT_PAY',
  BANK_TRANSFER: 'BANK_TRANSFER',
};

function PayModal({ visible, onCancel, onOk, balance = '¥0.00' }: any) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [amount, setAmount] = useState(100);
  const [isCustom, setIsCustom] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHODS.BANK_TRANSFER);
  const [form] = Form.useForm();
  const [wechatPayUrl, setWechatPayUrl] = useState('');
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [rechargeStatus, setRechargeStatus] = useState<RechargeStatus>();
  const [loading, setLoading] = useState(false);
  const [id, setId] = useState('');
  const [sysConfig, setSysConfig] = useState<any>();
  const timer = useRef(null);

  const getSysConf = async () => {
    try {
      setLoading(true);
      const res = await getSystemConfig();
      setSysConfig(res);
    } catch (error) {
      console.error('获取转账信息失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    clear();
    if (visible) {
      reset();
      getSysConf();
    }
  }, [visible]);

  // 二维码超时直接关闭弹窗
  useEffect(() => {
    if (sysConfig?.enabledWechatPay) {
      setPaymentMethod(PAYMENT_METHODS.WECHAT_PAY);
    }
    const clear1 = () => timer.current && clearTimeout(timer.current);
    clear1();
    const timeout = sysConfig?.wechatPayConfig?.timeout;
    if (!timeout) return;
    const time = timeout * 60 * 1000; // 二维码超时时间
    timer.current = setTimeout(() => {
      handleCancel();
      clearTimeout(timer.current);
    }, time);
    return clear1;
  }, [sysConfig]);

  const clear = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const handleCancel = async () => {
    try {
      if (id) {
        const { status } = await getRechargeStatus(id);
        if (status === RECHARGE_STATUS.PENDING) await cancelRecharge(id);
      }
      onCancel();
    } catch (error) {
      console.error('取消充值失败:', error);
    } finally {
      onCancel();
    }
  };

  // 创建微信充值充值申请
  const createWechatPayRecharge = async () => {
    try {
      setLoading(true);
      const values = {
        amount: String(amount),
        currency: 'CNY',
        operator: getUserInfo()?.name,
        paymentMethod: PAYMENT_METHODS.WECHAT_PAY,
      };

      const res = await rechargeRequests(values);

      if (res.paymentCodeUrl) {
        setWechatPayUrl(res.paymentCodeUrl);
        setId(res.id);
        startStatusPolling(res.id);
      } else {
        Message.error(t('tenant:failed.to.get.payment.link'));
      }
    } catch (error) {
      console.error('创建微信充值申请失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 开始轮询充值状态
  const startStatusPolling = (id: string) => {
    clear();
    intervalRef.current = setInterval(async () => {
      try {
        const statusRes = await getRechargeStatus(id);
        setRechargeStatus(statusRes.status);
        if (statusRes.status === RECHARGE_STATUS.APPROVED) {
          Message.success(t('common:recharge.successful'));
          clearInterval(intervalRef.current);
          onOk(); // 通知父组件充值成功
        } else if (statusRes.status === RECHARGE_STATUS.REJECTED) {
          Message.error(`${t('common:recharge.failed')}: ${statusRes.rejectReason || t('common:unknown.reason')}`);
          clearInterval(intervalRef.current);
        }
      } catch (error) {
        Message.error(`${t('common:recharge.failed')}: ${error.message || t('common:unknown.reason')}`);
      }
    }, 3000); // 每3秒轮询一次
  };

  const handleSubmit = async () => {
    try {
      if (paymentMethod === PAYMENT_METHODS.BANK_TRANSFER) {
        // 对公转账逻辑
        await form.validate();
        let values = await form.getFieldsValue();
        values = {
          ...values,
          amount: String(amount),
          currency: 'CNY',
          operator: getUserInfo()?.name,
          paymentMethod: PAYMENT_METHODS.BANK_TRANSFER,
        };
        console.log(values, 'values');
        const res = await rechargeRequests(values);
        Message.success(t('tenant:submitted.successfully'));
        onOk(values);
      } else {
        // 支付完成
        onOk(null);
      }
    } catch (error) {
      Message.error(t('tenant:please.complete.all.fields'));
    }
  };

  const reset = () => {
    setStep(1);
    setAmount(100);
    setIsCustom(false);
    setInputValue('');
    setPaymentMethod(PAYMENT_METHODS.BANK_TRANSFER);
    form.resetFields();
  };

  const handleAmountSelect = (selectedAmount) => {
    setAmount(selectedAmount);
    setIsCustom(false);
  };

  const handleCustomAmountChange = (value) => {
    setInputValue(value);
    setAmount(value);
    setIsCustom(true);
  };

  const handleNextStep = async () => {
    if (amount <= 0) {
      Message.error(t('tenant:recharge.amount.must.be.greater.than.0'));
      return;
    }
    setStep(2);
  };

  // 请求位置支付二维码链接
  useEffect(() => {
    if (paymentMethod === PAYMENT_METHODS.WECHAT_PAY && step === 2 && !wechatPayUrl) {
      createWechatPayRecharge();
    }
  }, [step, paymentMethod, wechatPayUrl]);

  const handlePrevStep = () => {
    setStep(1);
  };

  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);
  };

  // 金额选择组件
  const renderAmountSelection = () => (
    <div>
      <h3 style={{ marginTop: 0 }}>
        {t('tenant:current.balance')}
        {balance}
      </h3>

      <div>
        <h3>
          {t('tenant:recharge.amount.with.currency.symbol')}
          {amount}
        </h3>
        <div style={{ display: 'flex', gap: 16 }}>
          {PRESET_AMOUNTS.map((item) => (
            <AmountCard key={item} amount={item} isSelected={!isCustom && amount === item} onClick={() => handleAmountSelect(item)} />
          ))}
          <CustomAmountInput value={inputValue} isSelected={isCustom} onChange={handleCustomAmountChange} onSelect={setIsCustom} />
        </div>
      </div>

      <div>
        <h3 style={{ marginTop: 20 }}>{t('tenant:recharge.method')}</h3>
        <Space size={40} direction="horizontal">
          {sysConfig?.enabledWechatPay && (
            <Radio
              checked={paymentMethod === PAYMENT_METHODS.WECHAT_PAY}
              onChange={() => handlePaymentMethodChange(PAYMENT_METHODS.WECHAT_PAY)}
            >
              {t('common:wechat.recharge')}
              <p style={{ color: '#999', fontSize: 12 }}>{t('tenant:instant.credit.scan.qr.to.pay')}</p>
            </Radio>
          )}
          <Radio
            checked={paymentMethod === PAYMENT_METHODS.BANK_TRANSFER}
            onChange={() => handlePaymentMethodChange(PAYMENT_METHODS.BANK_TRANSFER)}
          >
            {t('common:corporate.transfer')}
            <p style={{ color: '#999', fontSize: 12 }}>{t('tenant:bank.corporate.transfer.review.notice')}</p>
          </Radio>
        </Space>
      </div>
    </div>
  );

  // 转账信息组件
  const renderTransferInfo = () => {
    if (paymentMethod === PAYMENT_METHODS.WECHAT_PAY) {
      return renderWechatPayInfo();
    } else {
      return renderBankTransferInfo();
    }
  };

  // 微信充值信息
  const renderWechatPayInfo = () => {
    return (
      <div style={{ textAlign: 'center' }}>
        <WechatPayQRCode
          payUrl={wechatPayUrl}
          onGenerate={(url) => console.log('QR Code generated for:', url)}
          loading={loading}
          payStatus={rechargeStatus}
          timeout={sysConfig?.wechatPayConfig?.timeout}
        />
      </div>
    );
  };

  // 对公转账信息
  const renderBankTransferInfo = () => (
    <div>
      <h3>{t('common:corporate.transfer')}</h3>
      <div style={{ display: 'flex', gap: 16 }}>
        <TransferInfoSection amount={amount} data={sysConfig || {}} />
        <CompanyInfoForm form={form} />
      </div>
    </div>
  );

  // 底部按钮组件
  const renderFooter = () => (
    <div style={footerStyle}>
      {paymentMethod === PAYMENT_METHODS.WECHAT_PAY && wechatPayUrl ? (
        <Popconfirm focusLock title={t('tenant:confirm.cancel.payment')} onOk={handleCancel}>
          <Button>{t('common:cancel')}</Button>
        </Popconfirm>
      ) : (
        <Button onClick={handleCancel}>{t('common:cancel')}</Button>
      )}
      {step < 2 ? (
        <Button type="primary" onClick={handleNextStep} loading={loading && paymentMethod === PAYMENT_METHODS.WECHAT_PAY}>
          {t('cluster:next')}
        </Button>
      ) : (
        <>
          {paymentMethod !== PAYMENT_METHODS.WECHAT_PAY && <Button onClick={handlePrevStep}>{t('cluster:pre')}</Button>}
          <Button type="primary" onClick={handleSubmit}>
            {paymentMethod === PAYMENT_METHODS.WECHAT_PAY ? t('tenant:payment.completed') : t('common:submit')}
          </Button>
        </>
      )}
    </div>
  );

  return (
    <CustomModal
      title={t('common:recharge')}
      visible={visible}
      onCancel={handleCancel}
      maskClosable={false}
      style={{
        width: '80vw',
        minWidth: paymentMethod === PAYMENT_METHODS.WECHAT_PAY ? 500 : 600,
        maxWidth: 1200,
      }}
      footer={null}
    >
      <Spin loading={loading} block>
        {step < 2 ? renderAmountSelection() : renderTransferInfo()}
      </Spin>
      {renderFooter()}
    </CustomModal>
  );
}

// 金额卡片组件
const AmountCard = ({ amount, isSelected, onClick }) => {
  const { t } = useTranslation();
  return (
    <div
      style={{
        ...amountCardStyle,
        backgroundColor: isSelected ? 'rgb(var(--primary-1))' : 'transparent',
        borderColor: isSelected ? 'rgb(var(--primary-6))' : '#ccc',
      }}
      onClick={onClick}
    >
      <p>¥ {amount}</p>
      <p style={{ color: '#999', fontSize: 12 }}>
        {t('tenant:balance.plus')}
        {amount}
      </p>
    </div>
  );
};

const CustomNumInput = styled(InputNumber)`
  border-width: 0px !important;
  height: 100%;
  .arco-input-group {
    border-width: 0px !important;
    height: 100%;
    background-color: transparent;
  }
  .arco-input-inner-wrapper {
    border-width: 0px !important;
    height: 100%;
    background-color: transparent;
  }
`;

// 自定义金额输入组件
const CustomAmountInput = ({ value, isSelected, onChange, onSelect }) => (
  <div
    style={{
      ...amountCardStyle,
      backgroundColor: isSelected ? 'rgb(var(--primary-1))' : 'transparent',
      borderColor: isSelected ? primaryColor : '#ccc',
      flexDirection: 'row',
    }}
    onClick={() => onSelect(true)}
  >
    ¥
    <CustomNumInput placeholder={t('tenant:other.amount')} onChange={onChange} value={value} precision={2} />
  </div>
);

// 转账信息区域组件
const TransferInfoSection = ({ amount, data }) => {
  const { t } = useTranslation();
  const formatTransferInfo = () => {
    const info = [
      `${t('tenant:company.full.name')}：${data.companyName || ''}`,
      `${t('common:bank.account')}：${data.bankAccount || ''}`,
      `${t('common:bank.name')}：${data.bankName || ''}`,
      `${t('common:transfer.note')}：${data.transferRemark || ''}`,
      `${t('tenant:transfer.amount')}：¥${amount}`,
    ];
    return info.join('\n');
  };

  const copyTransferInfo = async () => {
    try {
      const transferInfo = formatTransferInfo();
      if (copy(transferInfo)) {
        Message.success(t('common:copied.successfully'));
      } else {
        Message.error(t('tenant:copy.failed.please.copy.manually'));
      }
    } catch (error) {
      console.error('复制失败:', error);
      Message.error(t('tenant:copy.failed.please.copy.manually'));
    }
  };

  return (
    <div style={{ flex: 1 }}>
      <h4>{t('tenant:step.1.corporate.transfer')}</h4>
      <div style={transferInfoStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>{t('tenant:remittance.information')}</span>
          <Button type="text" onClick={copyTransferInfo}>
            {t('tenant:copy.remittance.information')}
          </Button>
        </div>
        <InfoRow label={t('tenant:company.full.name')} value={data.companyName} />
        <InfoRow label={t('common:bank.account')} value={data.bankAccount} />
        <InfoRow label={t('common:bank.name')} value={data.bankName} />
        <InfoRow label={t('common:transfer.note')} value={data.transferRemark} />
        <InfoRow label={t('tenant:transfer.amount')} value={`¥ ${amount}`} />
      </div>
    </div>
  );
};

// 信息行组件
const InfoRow = ({ label, value }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr' }}>
    <span>{label}</span>
    <span>{value}</span>
  </div>
);

// 公司信息表单组件
const CompanyInfoForm = ({ form }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [id, setId] = useState('');

  const getId = async () => {
    try {
      setLoading(true);
      const res = await preCreateId();
      setId(res?.id);
      form.setFieldValue('id', res?.id);
    } catch (error) {
      Message.error(t('tenant:failed.to.create.id'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getId();
  }, [form]);

  return (
    <div style={{ flex: 1 }}>
      <h4>{t('tenant:step.2.fill.company.info.and.upload.receipt')}</h4>
      <Spin loading={loading} block>
        <Form form={form}>
          <Form.Item label="ID" field="id" disabled>
            <Input />
          </Form.Item>
          <Form.Item
            label={t('common:transferor')}
            field="companyName"
            rules={[{ required: true, message: t('tenant:please.enter.transferor') }]}
          >
            <Input placeholder={t('tenant:please.enter.transferor')} />
          </Form.Item>
          <Form.Item
            label={t('common:contact.phone')}
            field="phoneNumber"
            rules={[
              { required: true, message: t('tenant:please.enter.contact.phone') },
              {
                match: /^1[3456789]\d{9}$/,
                message: t('common:please.enter.a.valid.phone.number'),
              },
            ]}
          >
            <Input placeholder={t('tenant:please.enter.contact.phone')} />
          </Form.Item>
          <Form.Item
            label={t('tenant:upload.payment.receipt')}
            field="voucher"
            rules={[{ required: true, message: t('tenant:please.upload.payment.receipt') }]}
          >
            <ImageUploadItem
              data={{ id }}
              showName={false}
              fieldName=""
              maxCount={1}
              tokenScope="dc"
              onUploadSuccess={(file) => {
                if (file) {
                  form.setFieldValue('voucher', file.name);
                }
              }}
            />
          </Form.Item>
          <Form.Item label={t('common:remarks')} field="remark">
            <Input.TextArea />
          </Form.Item>
        </Form>
      </Spin>
    </div>
  );
};

// 样式常量
const amountCardStyle: any = {
  border: '1px solid #ccc',
  padding: 16,
  borderRadius: 4,
  cursor: 'pointer',
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 16,
  flexDirection: 'column',
};

const transferInfoStyle = {
  backgroundColor: '#f9f9f9',
  padding: 16,
  borderRadius: 4,
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: 10,
};

const footerStyle = {
  display: 'flex',
  gap: 16,
  justifyContent: 'flex-end',
  borderTop: '1px solid #eee',
  margin: '0 -24px',
  paddingTop: 24,
  paddingRight: 24,
  marginTop: 32,
};

export default PayModal;
